/**
 * Hook that exposes the reports microservice as a small set of typed
 * methods.
 *
 * We deliberately don't add these to the global AppContext because:
 *  - the reports surface is self-contained inside the Reports page,
 *  - AppContext is already a 3000-line file we'd rather not grow.
 *
 * fetchWithAuth comes from AppContext so we keep the same auth /
 * client-session-id headers as every other request.
 */

import { useCallback } from "react";
import { getApiUrl } from "../config/api";
import { useApp } from "../context/AppContext";
import {
  AddSalonReportRequest,
  CreateReportSharesRequest,
  ReportDefinitionsResponse,
  ReportShare,
  ReportSharesResponse,
  ReportRunResult,
  RunReportRequest,
  SalonReport,
  SalonReportsResponse,
  UpdateSalonReportRequest,
} from "../types/reports";
import { RunReportV2Request, RunReportV2Response } from "../types/reportsV2";

export function useReports() {
  // useApp is the canonical way to grab fetchWithAuth. The cast keeps
  // us off the giant AppContext interface for the few props we use.
  const { fetchWithAuth } = useApp() as unknown as {
    fetchWithAuth: (url: string, options?: RequestInit) => Promise<unknown>;
  };

  const listDefinitions =
    useCallback(async (): Promise<ReportDefinitionsResponse> => {
      const url = getApiUrl("REPORT_DEFINITIONS");
      return (await fetchWithAuth(url, {
        method: "GET",
      })) as ReportDefinitionsResponse;
    }, [fetchWithAuth]);

  const listSalonReports = useCallback(
    async (salonId: string): Promise<SalonReportsResponse> => {
      const url = getApiUrl("SALON_REPORTS", "", { salonId });
      return (await fetchWithAuth(url, {
        method: "GET",
      })) as SalonReportsResponse;
    },
    [fetchWithAuth],
  );

  const addSalonReport = useCallback(
    async (
      salonId: string,
      payload: AddSalonReportRequest,
    ): Promise<{ report: SalonReport }> => {
      const url = getApiUrl("SALON_REPORTS", "", { salonId });
      return (await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify(payload),
      })) as { report: SalonReport };
    },
    [fetchWithAuth],
  );

  // PUT accepts any subset of UpdateSalonReportRequest — including the Reports
  // v2 shell's `custom_description` (and, for v2 report types, `view_config`).
  const updateSalonReport = useCallback(
    async (
      salonId: string,
      reportId: string,
      payload: UpdateSalonReportRequest,
    ): Promise<{ report: SalonReport }> => {
      const url = getApiUrl("SALON_REPORT_BY_ID", "", { salonId, reportId });
      return (await fetchWithAuth(url, {
        method: "PUT",
        body: JSON.stringify(payload),
      })) as { report: SalonReport };
    },
    [fetchWithAuth],
  );

  // Shared copies of the report are kept unless deleteSharedCopies is set.
  const deleteSalonReport = useCallback(
    async (
      salonId: string,
      reportId: string,
      { deleteSharedCopies = false }: { deleteSharedCopies?: boolean } = {},
    ): Promise<void> => {
      const url = getApiUrl(
        "SALON_REPORT_BY_ID",
        deleteSharedCopies ? "delete_shared_copies=true" : "",
        { salonId, reportId },
      );
      await fetchWithAuth(url, { method: "DELETE" });
    },
    [fetchWithAuth],
  );

  const listReportShares = useCallback(
    async (
      salonId: string,
      reportId: string,
    ): Promise<ReportSharesResponse> => {
      const url = getApiUrl("SALON_REPORT_SHARES", "", { salonId, reportId });
      return (await fetchWithAuth(url, {
        method: "GET",
      })) as ReportSharesResponse;
    },
    [fetchWithAuth],
  );

  // Resolves to every share of the report (the new ones included).
  const createReportShares = useCallback(
    async (
      salonId: string,
      reportId: string,
      payload: CreateReportSharesRequest,
    ): Promise<{ shares: ReportShare[] }> => {
      const url = getApiUrl("SALON_REPORT_SHARES", "", { salonId, reportId });
      return (await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify(payload),
      })) as { shares: ReportShare[] };
    },
    [fetchWithAuth],
  );

  const setReportShareSync = useCallback(
    async (
      salonId: string,
      reportId: string,
      shareId: string,
      syncWithSource: boolean,
    ): Promise<{ share: ReportShare }> => {
      const url = getApiUrl("SALON_REPORT_SHARE_BY_ID", "", {
        salonId,
        reportId,
        shareId,
      });
      return (await fetchWithAuth(url, {
        method: "PUT",
        body: JSON.stringify({ sync_with_source: syncWithSource }),
      })) as { share: ReportShare };
    },
    [fetchWithAuth],
  );

  // Revoking deletes the recipient's copy.
  const revokeReportShare = useCallback(
    async (
      salonId: string,
      reportId: string,
      shareId: string,
    ): Promise<void> => {
      const url = getApiUrl("SALON_REPORT_SHARE_BY_ID", "", {
        salonId,
        reportId,
        shareId,
      });
      await fetchWithAuth(url, { method: "DELETE" });
    },
    [fetchWithAuth],
  );

  const runSalonReport = useCallback(
    async (
      salonId: string,
      reportId: string,
      payload: RunReportRequest = {},
    ): Promise<ReportRunResult> => {
      const url = getApiUrl("RUN_SALON_REPORT", "", { salonId, reportId });
      return (await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify(payload),
      })) as ReportRunResult;
    },
    [fetchWithAuth],
  );

  // New-engine (v2) reports run on their own endpoint. Pass an AbortSignal
  // to cancel a superseded run (fetchWithAuth forwards it to fetch).
  // One endpoint for every v2 report: a table report answers with a
  // RunReportV2Response, a summary (ledger) report with a
  // RunLedgerReportResponse — the caller names which.
  const runSalonReportV2 = useCallback(
    async <T = RunReportV2Response>(
      salonId: string,
      reportId: string,
      payload: RunReportV2Request = {},
      { signal }: { signal?: AbortSignal } = {},
    ): Promise<T> => {
      const url = getApiUrl("RUN_SALON_REPORT_V2", "", { salonId, reportId });
      return (await fetchWithAuth(url, {
        method: "POST",
        body: JSON.stringify(payload),
        signal,
      })) as T;
    },
    [fetchWithAuth],
  );

  return {
    listDefinitions,
    listSalonReports,
    addSalonReport,
    updateSalonReport,
    deleteSalonReport,
    listReportShares,
    createReportShares,
    setReportShareSync,
    revokeReportShare,
    runSalonReport,
    runSalonReportV2,
  };
}
